import clsx from 'clsx'
import React, { useContext, useEffect } from 'react'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { handleLabelNameChanges } from 'src/cms/helpers/automation'
import { KTSVG } from '../../../helpers'
import { isMenuActive } from './menuActive'
import { ActiveMenuContex } from '../../MasterLayout'
import { useLayout } from '../../core'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  /** Locally resolved icon (see menuIcons.tsx). Takes precedence over `icon`/`fontIcon`. */
  iconNode?: React.ReactNode
  /** 'parent' = top-level module, 'child' = item inside a group. Drives the icon treatment. */
  variant?: 'parent' | 'child'
}

const isExternalUrl = (url: string): boolean => /^https?:\/\//i.test(url)

const AsideMenuItem: React.FC<Props> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet = false,
  iconNode,
  variant = 'parent',
}) => {
  const { pathname } = useLocation()
  const isActive = !isExternalUrl(to) && isMenuActive(pathname, to)
  const { config } = useLayout()
  const activeMenu: any = useContext(ActiveMenuContex)
  const { aside } = config
  const isChild = variant === 'child'

  useEffect(() => {
    if (isActive) {
      activeMenu?.setCurrentActiveMenu(title);
      sessionStorage.setItem('activeMenu', title)
    }
  }, [isActive])

  const linkClass = clsx('menu-link without-sub gibl-menu-link', {
    active: isActive,
    'gibl-menu-link--child': isChild,
    'gibl-menu-link--parent': !isChild,
  })

  const commonContent = (
    <>
      {/* Children are marked by the dot on the group's guide rail (CSS ::before),
          so they render no icon of their own. */}
      {!isChild && iconNode && <span className='menu-icon gibl-menu-icon'>{iconNode}</span>}
      {!iconNode && hasBullet && (
        <span className='menu-bullet gibl-menu-bullet'>
          <span className='bullet bullet-dot'></span>
        </span>
      )}
      {!iconNode && icon && aside.menuIcon === 'svg' && (
        <span className='menu-icon gibl-menu-icon'>
          <KTSVG path={icon} className='svg-icon-2' />
        </span>
      )}
      {!iconNode && fontIcon && (
        <span className='menu-icon gibl-menu-icon'>
          <i className={clsx('bi', isChild ? 'fs-5' : 'fs-3', fontIcon)}></i>
        </span>
      )}
      <span className='menu-title'>{title}</span>
    </>
  )

  return (
    <div className={clsx('menu-item', { 'gibl-menu-item--child': isChild })}>
      {isExternalUrl(to) ? (
        <a
          href={to}
          target='_blank'
          rel='noopener noreferrer'
          className={linkClass}
          data-cy={`${handleLabelNameChanges(title)}-menu-item`}
          onClick={() => activeMenu?.setOpen(!activeMenu?.open)}
          title={title}
        >
          {commonContent}
        </a>
      ) : (
        <Link
          to={to}
          className={linkClass}
          data-cy={`${handleLabelNameChanges(title)}-menu-item`}
          onClick={() => activeMenu?.setOpen(!activeMenu?.open)}
          title={title}
          aria-current={isActive ? 'page' : undefined}
        >
          {commonContent}
        </Link>
      )
      }
      {children}
    </div >
  )
}

export { AsideMenuItem }
